import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase.js'
import { apiFetch } from '../lib/api.js'
import { pageBg as podiumBg } from '../lib/images'

const NIVEAUX = [
  'CP', 'CE1', 'CE2', 'CM1', 'CM2',
  '6e', '5e', '4e', '3e', 'CAP',
  '2nde', '1ère', 'Terminale',
  'Supérieur', 'Formation continue', 'Non applicable',
]

const PROFILE_TYPES = [
  { value: 'enseignant-formateur', label: 'Enseignant / Formateur', emoji: '🧑‍🏫' },
  { value: 'eleve-apprenant', label: 'Élève / Apprenant', emoji: '🎒' },
  { value: 'parent', label: 'Parent', emoji: '👪' },
  { value: 'autre', label: 'Autre', emoji: '👤' },
]

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1) // 1 = identité, 2 = champs conditionnels
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Champs identité
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [profileType, setProfileType] = useState('')

  // Champs conditionnels
  const [etablissement, setEtablissement] = useState('')
  const [codePostal, setCodePostal] = useState('')
  const [niveauxSelected, setNiveauxSelected] = useState([]) // multi pour enseignant
  const [niveauSingle, setNiveauSingle] = useState('')       // mono pour élève

  const [userEmail, setUserEmail] = useState('')

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate('/login', { replace: true }); return }

      // Vérifier si déjà complété
      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_completed, role')
        .eq('id', session.user.id)
        .single()

      if (profile?.onboarding_completed) {
        navigate('/home', { replace: true })
        return
      }

      // Email — session.user.email en priorité, fallback sur les métadonnées
      // (Azure AD peut stocker l'email dans preferred_username si le scope email est absent)
      const meta = session.user.user_metadata || {}
      const idData = session.user.identities?.[0]?.identity_data || {}
      const email = session.user.email
        || meta.email || meta.preferred_username
        || idData.email || idData.preferred_username
        || ''
      setUserEmail(email)

      // Pré-remplir depuis les métadonnées SSO
      const givenName = meta.given_name || idData.given_name || ''
      const familyName = meta.family_name || idData.family_name || ''
      const fullName = meta.full_name || meta.name || idData.name || idData.full_name || ''

      if (!prenom) {
        if (givenName) {
          setPrenom(givenName)
        } else if (fullName) {
          const parts = fullName.trim().split(' ')
          setPrenom(parts[0] || '')
        }
      }
      if (!nom) {
        if (familyName) {
          setNom(familyName)
        } else if (fullName) {
          const parts = fullName.trim().split(' ')
          if (parts.length > 1) setNom(parts.slice(1).join(' '))
        }
      }
    }
    init()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleNiveau(niveau) {
    setNiveauxSelected(prev =>
      prev.includes(niveau) ? prev.filter(n => n !== niveau) : [...prev, niveau]
    )
  }

  function hasConditionalFields() {
    return ['enseignant-formateur', 'eleve-apprenant', 'parent'].includes(profileType)
  }

  function handleStep1(e) {
    e.preventDefault()
    if (!profileType) { setError('Choisis un type de profil.'); return }
    setError('')
    if (hasConditionalFields()) {
      setStep(2)
    } else {
      handleSubmit()
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault()
    setError('')
    setLoading(true)

    const updates = {
      prenom: prenom.trim(),
      nom: nom.trim(),
      profile_type: profileType,
      onboarding_completed: true,
    }

    if (hasConditionalFields()) {
      updates.etablissement = etablissement.trim() || null
      updates.code_postal = codePostal.trim() || null
      if (profileType === 'enseignant-formateur') {
        updates.niveaux = niveauxSelected.length > 0 ? niveauxSelected : null
      } else if (profileType === 'eleve-apprenant') {
        updates.niveaux = niveauSingle ? [niveauSingle] : null
      }
    }

    try {
      await apiFetch('/api/profile', { method: 'PATCH', body: JSON.stringify(updates) })
      navigate('/home', { replace: true })
    } catch {
      setError('Erreur lors de la sauvegarde. Réessaie.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{ backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-lg">
        {/* En-tête */}
        <div className="text-center mb-6">
          <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-12 h-12 mx-auto mb-2 drop-shadow rounded-lg" />
          <h1 className="text-2xl font-extrabold text-white">Bienvenue sur KartQuiz !</h1>
          <p className="text-white/60 text-sm mt-1">Dis-nous en un peu plus sur toi</p>
        </div>

        <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl p-6">
          {/* Indicateur d'étapes */}
          {hasConditionalFields() && (
            <div className="flex items-center gap-2 mb-5">
              <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-white/20'}`} />
              <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-white/20'}`} />
            </div>
          )}

          {/* Étape 1 — Identité & type de profil */}
          {step === 1 && (
            <form onSubmit={handleStep1} className="space-y-4">
              {userEmail && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Adresse email</label>
                  <input
                    type="text"
                    value={userEmail}
                    readOnly
                    className="w-full border border-white/10 bg-white/20 text-white/60 rounded-lg px-3 py-2 text-sm cursor-not-allowed"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={prenom}
                    onChange={e => setPrenom(e.target.value)}
                    required
                    className="w-full border border-white/20 bg-white/90 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Prénom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">NOM</label>
                  <input
                    type="text"
                    value={nom}
                    onChange={e => setNom(e.target.value)}
                    required
                    className="w-full border border-white/20 bg-white/90 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Nom de famille"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Je suis…</label>
                <div className="grid grid-cols-2 gap-2">
                  {PROFILE_TYPES.map(pt => (
                    <button
                      key={pt.value}
                      type="button"
                      onClick={() => setProfileType(pt.value)}
                      className={`flex items-center gap-2 border-2 rounded-xl px-3 py-3 text-sm font-medium transition ${
                        profileType === pt.value
                          ? 'border-primary bg-primary/20 text-white'
                          : 'border-white/20 text-white/80 hover:border-white/40'
                      }`}
                    >
                      <span className="text-xl">{pt.emoji}</span>
                      <span>{pt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-2.5 disabled:opacity-60"
              >
                {hasConditionalFields() ? 'Suivant →' : loading ? '…' : 'Commencer'}
              </button>
            </form>
          )}

          {/* Étape 2 — Champs conditionnels */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">
                  {profileType === 'parent' ? 'Établissement de ton enfant' : 'Établissement'}
                </label>
                <input
                  type="text"
                  value={etablissement}
                  onChange={e => setEtablissement(e.target.value)}
                  className="w-full border border-white/20 bg-white/90 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Nom de l'établissement (optionnel)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Code postal</label>
                <input
                  type="text"
                  value={codePostal}
                  onChange={e => setCodePostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="w-full border border-white/20 bg-white/90 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="75000 (optionnel)"
                  maxLength={5}
                />
              </div>

              {profileType === 'enseignant-formateur' && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Niveaux enseignés</label>
                  <div className="flex flex-wrap gap-1.5">
                    {NIVEAUX.map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleNiveau(n)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                          niveauxSelected.includes(n)
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white/10 text-white/70 border-white/20 hover:border-primary'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {profileType === 'eleve-apprenant' && (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Niveau scolaire</label>
                  <select
                    value={niveauSingle}
                    onChange={e => setNiveauSingle(e.target.value)}
                    className="w-full border border-white/20 bg-white/90 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">— Choisir —</option>
                    {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}

              {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{error}</p>}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 border border-white/20 rounded-lg py-2.5 text-sm font-medium text-white/70 hover:bg-white/10 transition"
                >
                  ← Retour
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary py-2.5 disabled:opacity-60"
                >
                  {loading ? '…' : 'Commencer'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

